import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { CustomerForNotification } from 'types/customer';

/**
 * 顧客一覧の EintityAdapter
 */
const customersAdapter = createEntityAdapter<CustomerForNotification>({
  selectId: (customer) => customer.id,
  sortComparer: (a, b) => a.id - b.id,
});

/**
 * 顧客一覧の EintityState に外挿する状態
 */
type ExtraState = {
  /**
   * 顧客一覧のロード中フラグ
   */
  loading: boolean;
  /**
   * 顧客 ID の検索フィールド
   * NOTE: 顧客 ID は number だが検索フォームは string なのでここは string 型にしておく
   */
  id: string;
  /**
   * 顧客名の検索フィールド
   */
  name: string;
  /**
   * 顧客詳細の検索フィールド
   */
  detail: string;
};

/**
 * 顧客一覧の EintityState に外挿する状態の初期値
 */
const initExtraState: ExtraState = {
  loading: false,
  id: '',
  name: '',
  detail: '',
};

/**
 * 顧客一覧の slice(reducer + action)
 */
export const customersSlice = createSlice({
  name: 'customers',
  initialState: customersAdapter.getInitialState(initExtraState),
  reducers: {
    init: () => customersAdapter.getInitialState(initExtraState),
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    idChanged: (
      state,
      action: PayloadAction<{
        id: string;
      }>,
    ) => {
      state.id = action.payload.id;
    },
    nameChanged: (
      state,
      action: PayloadAction<{
        name: string;
      }>,
    ) => {
      state.name = action.payload.name;
    },
    detailChanged: (
      state,
      action: PayloadAction<{
        detail: string;
      }>,
    ) => {
      state.detail = action.payload.detail;
    },
    fetchCustomersSuccess: customersAdapter.setAll,
    customerUpdated: customersAdapter.updateOne,
    customersUpdated: customersAdapter.updateMany,
  },
});

/**
 * 顧客一覧の action
 */
export const customersActions = customersSlice.actions;

/**
 * 顧客一覧の selectors（内部用）
 */
const { selectAll, selectTotal } = customersAdapter.getSelectors<RootState>(
  (state) => state.notification.customers,
);
const getLoading = (state: RootState) => state.notification.customers.loading;
const getId = (state: RootState) => state.notification.customers.id;
const getName = (state: RootState) => state.notification.customers.name;
const getDetail = (state: RootState) => state.notification.customers.detail;
// ID，顧客名，顧客詳細でフィルタリングされた顧客一覧
const getFilteredCustomers = createSelector(
  selectAll,
  getId,
  getName,
  getDetail,
  (customers, id, name, detail) =>
    customers
      .filter((customer) => customer.id.toString().indexOf(id) !== -1)
      .filter((customer) => customer.name.indexOf(name) !== -1)
      .filter((customer) => customer.detail.indexOf(detail) !== -1),
);
// 選択された顧客一覧
const getSelectedCustomers = createSelector(selectAll, (customers) =>
  customers.filter((customer) => customer.isSelected),
);
// 選択された顧客一覧の要素数
const getSelectedCustomersLength = createSelector(
  selectAll,
  (customers) => customers.filter((customer) => customer.isSelected).length,
);
// チェックボックスが全てチェックされていたら true
const getIsSelectedAll = createSelector(getFilteredCustomers, (customers) =>
  customers.every((customer) => customer.isSelected),
);
// チェックボックスが１つ以上全て未満チェックされていたら true
const getIsIndeterminate = createSelector(
  getFilteredCustomers,
  getIsSelectedAll,
  (customers, isSelectedAll) =>
    customers.some((customer) => customer.isSelected) && !isSelectedAll,
);

/**
 * 顧客一覧の selectors
 */
export const customersSelectors = {
  loading: getLoading,
  id: getId,
  name: getName,
  detail: getDetail,
  total: selectTotal,
  filteredCustomers: getFilteredCustomers,
  selectedCustomers: getSelectedCustomers,
  selectedCustomersLength: getSelectedCustomersLength,
  isSelectedAll: getIsSelectedAll,
  isIndeterminate: getIsIndeterminate,
} as const;
